/**
 * WebAudioRenderer.jsx
 * Hidden Audio Element for Web Browser Playback
 * 
 * This component provides the actual audio playback capability for web browsers.
 * It subscribes to ClientTransportHandler events and controls a hidden <audio> element.
 * 
 * NOTE: This should NOT be rendered on iOS as native Swift handles audio there.
 * 
 * TRACK TRANSITION MODEL:
 * When switching tracks mid-playback, a single atomic transition owns the audio element:
 *   1. Fade volume → 0 (80ms)
 *   2. Pause + swap source
 *   3. Wait for canplay
 *   4. Play at volume 0, fade → user volume (100ms)
 * During this window, onPlaybackStateChanged is locked out to prevent play() races.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import Hls from 'hls.js';
import { getClientTransport } from '../handlers/ClientTransportHandler';
import { useMaestroStore, useActiveZone } from '../store/maestroStore';
import { TransportCommand } from '../transport/TransportCommand';
import { rlog } from '../utils/remoteLogger';
import { applyUsbDacToAudio } from '../hooks/useAutoUsbDac';
import { isConnectorAvailable } from '../lib/connector';
import { resolveLocalRendererStreamUrl } from '../lib/playback-bridge';

// ---------------------------------------------------------------------------
// Smooth fade — ramps audio.volume using setInterval for fine-grained steps.
// setInterval at ~5ms gives ~16 steps per 80ms (smoother than rAF's ~5 steps).
// Returns a Promise; pass a ref so callers can cancel mid-fade.
// ---------------------------------------------------------------------------
function smoothFade(audio, from, to, ms, timerRef) {
  // Cancel any running fade
  if (timerRef.current) {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }
  if (!audio || ms <= 0) {
    if (audio) audio.volume = Math.max(0, Math.min(1, to));
    return Promise.resolve();
  }
  return new Promise(resolve => {
    const start = performance.now();
    const tick = () => {
      const elapsed = performance.now() - start;
      const t = Math.min(1, elapsed / ms);
      audio.volume = Math.max(0, Math.min(1, from + (to - from) * t));
      if (t >= 1) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        resolve();
      }
    };
    // Immediate first tick so fade starts NOW (no 5ms delay)
    tick();
    timerRef.current = setInterval(tick, 5);
  });
}

const FADE_OUT_MS = 80;
const FADE_IN_MS = 100;
const TRANSITION_SAFETY_MS = 4000;
const STALL_RECOVERY_DELAY_MS = 500;

function isNetworkRendererActive() {
  const zone = useMaestroStore.getState().discovery?.activeZone;
  if (!zone) return false;
  const t = zone.type;
  const tt = zone.metadata?.transport_type;
  return t === 'upnp' || t === 'airplay' || t === 'chromecast' || t === 'psaudio' || t === 'lua' || t === 'manual'
    || tt === 'upnp' || tt === 'bluesound' || tt === 'raat';
}

function shouldLogConnectorDiagnostics() {
  return typeof window !== 'undefined' && !!window.__MAESTRO_CONNECTOR__;
}

function getAudioSnapshot(audio) {
  if (!audio) {
    return {
      hasAudio: false,
    };
  }

  return {
    hasAudio: true,
    src: String(audio.src || ''),
    currentTime: Number.isFinite(audio.currentTime) ? audio.currentTime : null,
    duration: Number.isFinite(audio.duration) ? audio.duration : null,
    paused: audio.paused,
    ended: audio.ended,
    volume: audio.volume,
    muted: audio.muted,
    readyState: audio.readyState,
    networkState: audio.networkState,
    errorCode: audio.error?.code ?? null,
    errorMessage: audio.error?.message ?? null,
  };
}

function getPlayErrorDetails(error) {
  if (!error) return { error: null };
  return {
    name: error.name || 'Error',
    message: error.message || String(error),
    code: error.code ?? null,
  };
}

const WebAudioRenderer = () => {
  const audioRef = useRef(null);
  const clientTransport = getClientTransport();
  
  const storeVolume = useMaestroStore((s) => s.volume);
  const activeZone = useActiveZone();
  
  const currentSrcRef = useRef('');
  const hlsRef = useRef(null);
  const isSeeking = useRef(false);
  const pendingPlayRef = useRef(false);

  // --- Transition state ---
  // When non-null, a track transition owns the audio element.
  // Value is the target volume (0.0–1.0) to restore after the new track loads.
  // ALL other play()/pause() calls must check this and yield.
  const transitionRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const safetyTimerRef = useRef(null);

  const cancelTransition = useCallback(() => {
    if (fadeTimerRef.current) { clearInterval(fadeTimerRef.current); fadeTimerRef.current = null; }
    if (safetyTimerRef.current) { clearTimeout(safetyTimerRef.current); safetyTimerRef.current = null; }
    transitionRef.current = null;
  }, []);

  const logAudio = useCallback((message, extra = {}, level = 'log') => {
    if (!shouldLogConnectorDiagnostics()) return;
    const logger = console[level] || console.log;
    logger(`[WebAudioRenderer] ${message}`, {
      ...getAudioSnapshot(audioRef.current),
      ...extra,
    });
  }, []);

  const stripCacheBusters = useCallback((u) => {
    const url = String(u || '').trim();
    if (!url) return '';
    try {
      const parsed = new URL(url);
      parsed.searchParams.delete('_cb');
      parsed.searchParams.delete('cb');
      return parsed.toString();
    } catch {
      return url;
    }
  }, []);
  
  useEffect(() => {
    if (audioRef.current) {
      clientTransport.registerAudioElement(audioRef.current);
      TransportCommand.registerAudioElement(audioRef.current);
      if (!isConnectorAvailable()) {
        applyUsbDacToAudio(audioRef.current);
      }
    }
    return () => {
      clientTransport.unregisterAudioElement();
      TransportCommand.unregisterAudioElement();
    };
  }, []);

  // ---------------------------------------------------------------------------
  // AUDIO ELEMENT EVENT HANDLERS
  // ---------------------------------------------------------------------------

  const consecutiveErrorsRef = useRef(0);
  const remoteStreamRetryRef = useRef(null);

  // Stall watchdog: if audio stays in loading/waiting state >30s without progress, advance.
  // Raised from 8s to 30s — the old threshold caused premature track skips when album page
  // API calls temporarily starved the audio stream of bandwidth.
  const stallTimerRef = useRef(null);
  const lastProgressTimeRef = useRef(0);
  const stallRecoveryTimerRef = useRef(null);
  const stallRecoveryInFlightRef = useRef(false);

  const clearStallRecoveryTimer = useCallback(() => {
    if (stallRecoveryTimerRef.current) {
      clearTimeout(stallRecoveryTimerRef.current);
      stallRecoveryTimerRef.current = null;
    }
  }, []);

  const syncPositionFromAudio = useCallback(() => {
    const a = audioRef.current;
    if (!a || isSeeking.current) return;
    const dur = Number.isFinite(a.duration) && a.duration > 0
      ? a.duration
      : useMaestroStore.getState().duration;
    if (Number.isFinite(a.currentTime)) {
      useMaestroStore.getState().updateTime(a.currentTime, dur || 0);
    }
  }, []);

  const setStreamBuffering = useCallback((buffering) => {
    useMaestroStore.getState().setAudioStreamBuffering(buffering);
  }, []);

  const attemptStallRecovery = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || audio.ended || transitionRef.current !== null) return;
    if (useMaestroStore.getState().playbackState !== 'playing') return;
    if (isNetworkRendererActive()) return;

    const stillWaiting = audio.readyState < HTMLMediaElement.HAVE_FUTURE_DATA
      && !audio.paused
      && !audio.ended;
    if (!stillWaiting) return;

    if (stallRecoveryInFlightRef.current) return;
    stallRecoveryInFlightRef.current = true;

    const resumeAt = audio.currentTime;
    const src = audio.src;
    console.warn('[WebAudioRenderer] stall recovery — reload+play', {
      resumeAt,
      readyState: audio.readyState,
      networkState: audio.networkState,
    });
    rlog('WAR', 'stall-recovery', {
      resumeAt: Math.floor(resumeAt),
      readyState: audio.readyState,
      networkState: audio.networkState,
    });

    const finishRecovery = () => {
      stallRecoveryInFlightRef.current = false;
    };

    const onCanPlay = () => {
      audio.removeEventListener('canplay', onCanPlay);
      try {
        if (Number.isFinite(resumeAt) && resumeAt > 0) {
          audio.currentTime = resumeAt;
        }
      } catch { /* ignore seek errors during recovery */ }
      audio.play()
        .then(() => {
          finishRecovery();
          syncPositionFromAudio();
          try { clientTransport.startPositionBroadcast(); } catch {}
        })
        .catch((error) => {
          finishRecovery();
          logAudio('stall recovery play rejected', getPlayErrorDetails(error), 'warn');
        });
    };

    try {
      if (!src) {
        finishRecovery();
        return;
      }
      audio.addEventListener('canplay', onCanPlay);
      audio.load();
      setTimeout(() => {
        audio.removeEventListener('canplay', onCanPlay);
        if (stallRecoveryInFlightRef.current) {
          finishRecovery();
          logAudio('stall recovery timed out waiting for canplay', {}, 'warn');
        }
      }, 8000);
    } catch (e) {
      finishRecovery();
      logAudio('stall recovery failed', { error: e?.message || String(e) }, 'warn');
    }
  }, [clientTransport, logAudio, syncPositionFromAudio]);

  const clearStallWatchdog = useCallback(() => {
    if (stallTimerRef.current) { clearInterval(stallTimerRef.current); stallTimerRef.current = null; }
  }, []);

  const startStallWatchdog = useCallback(() => {
    clearStallWatchdog();
    lastProgressTimeRef.current = Date.now();
    stallTimerRef.current = setInterval(() => {
      const audio = audioRef.current;
      if (!audio || audio.ended) { clearStallWatchdog(); return; }
      // Only disarm if audio is paused AND the store agrees (user or system paused).
      // If store says 'playing' but audio is paused, that's the desync bug — don't disarm.
      if (audio.paused && useMaestroStore.getState().playbackState !== 'playing') {
        clearStallWatchdog(); return;
      }
      const elapsed = Date.now() - lastProgressTimeRef.current;
      if (elapsed > 30000) {
        // App was backgrounded — the large gap is from timer suspension, not a real stall.
        // Reset the anchor and give the stream a chance to resume.
        if (document.visibilityState === 'hidden') {
          lastProgressTimeRef.current = Date.now();
          return;
        }
        clearStallWatchdog();
        // Network renderer active — don't auto-advance locally
        try {
          const az = useMaestroStore.getState().discovery?.activeZone;
          if (az && az.type && az.type !== 'browser' && az.type !== 'local' && az.type !== 'local-native' && az.id !== 'this_computer' && az.id !== 'local_computer' && az.id !== 'this_device') {
            return;
          }
        } catch {}
        console.error('[WebAudioRenderer] Stall watchdog triggered — audio stuck for 30s, advancing');
        try {
          import('../transport/QueueController').then(({ QueueController }) => {
            QueueController.next();
          });
        } catch {}
      }
    }, 2000);
  }, [clearStallWatchdog]);

  const handleTimeUpdate = useCallback(() => {
    lastProgressTimeRef.current = Date.now();
    const a = audioRef.current;
    if (!a) return;
    syncPositionFromAudio();
    if (!a.paused && Math.floor(a.currentTime) % 30 === 0 && a.currentTime > 0) {
      rlog('WAR', 'heartbeat', { t: Math.floor(a.currentTime), dur: Math.floor(a.duration || 0), rs: a.readyState, ns: a.networkState });
    }
  }, [syncPositionFromAudio]);
  
  const handleEnded = useCallback(() => {
    const a = audioRef.current;
    rlog('WAR', 'ended', { t: Math.floor(a?.currentTime || 0), dur: Math.floor(a?.duration || 0), storeDur: useMaestroStore.getState().duration });
    const src = String(audioRef.current?.src || '');
    const isHls = /\.m3u8(\?|#|$)/i.test(src);

    if (isHls) {
      try {
        const store = useMaestroStore.getState();
        if (store.playbackState !== 'playing') return;
        if (!audioRef.current?.src) return;
        setTimeout(() => {
          try {
            if (useMaestroStore.getState().playbackState !== 'playing') return;
            const a = audioRef.current;
            if (!a?.src) return;
            a.load();
            a.play().catch(() => {});
          } catch {}
        }, 600);
      } catch {}
      return;
    }

    // When a network renderer is the active zone, MiniPlayer
    // handles track advancement — do NOT auto-advance locally or we get dual play.
    try {
      const az = useMaestroStore.getState().discovery?.activeZone;
      if (az && az.type && az.type !== 'browser' && az.type !== 'local' && az.type !== 'local-native' && az.id !== 'this_computer' && az.id !== 'local_computer' && az.id !== 'this_device') {
        return;
      }
    } catch {}

    // Ella Radio queue (System Audio / local): advance via QueueController and
    // leave the element idle — browser renderers replay the current src on ended.
    try {
      const isEllaRadioQueue = typeof sessionStorage !== 'undefined'
        && sessionStorage.getItem('active_ella_radio') === 'true';
      if (isEllaRadioQueue) {
        const store = useMaestroStore.getState();
        store.setPlaybackState('stopped');
        import('../transport/TransportCommand').then(({ TransportCommand }) => {
          TransportCommand.pauseLocalAudioElement();
        }).catch(() => {});
        import('../transport/QueueController').then(({ QueueController }) => {
          QueueController.advanceTrackEnd();
        }).catch(() => {});
        return;
      }
    } catch {}

    // Legacy radioStore surface (activeSource === 'radio').
    try {
      const store = useMaestroStore.getState();
      if (store.activeSource === 'radio') {
        store.setPlaybackState('stopped');
        return;
      }
    } catch {}

    // Delegate all queue advancement to QueueController so every advance
    // (local, remote, album, playlist) flows through the same pipeline with
    // format negotiation, skip-on-failure, and consistent error handling.
    try {
      import('../transport/QueueController').then(({ QueueController }) => {
        QueueController.next();
      });
    } catch {}
  }, []);
  
  const handleError = useCallback((e) => {
    const errCode = e.target.error?.code;
    const src = String(audioRef.current?.src || '');
    const pageOriginHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';

    const logStreamProbe = (hostKind) => {
      if (!src || !/^https?:\/\//i.test(src)) return;
      fetch(src, { method: 'HEAD', cache: 'no-store' })
        .then((resp) => {
          logAudio('stream HEAD probe', {
            hostKind,
            pageOriginHttps,
            httpStatus: resp.status,
          });
        })
        .catch((probeErr) => {
          logAudio(
            'stream HEAD probe failed',
            {
              hostKind,
              pageOriginHttps,
              probeError: probeErr?.message || String(probeErr),
            },
            'warn',
          );
        });
    };

    import('../lib/playback-bridge')
      .then(({ classifyStreamHostKind, emitLocalStreamHostKindDiagnostic }) => {
        import('../utils/platform').then(({ getTauriDesktopOs, isWindowsConnectorHost }) => {
        const hostKind = classifyStreamHostKind(src);
        const detectedOs = getTauriDesktopOs();
        const windowsHost = isWindowsConnectorHost();
        emitLocalStreamHostKindDiagnostic(src, src, {
          outcome: 'failure',
          context: 'WebAudioRenderer.handleError',
          error: e.target.error?.message || `code ${errCode}`,
        });
        rlog('WAR', 'error', {
          code: errCode,
          msg: e.target.error?.message || '',
          hostKind,
          pageOriginHttps,
          detectedOs,
          windowsHost,
        });
        logAudio('audio error event', {
          eventErrorCode: errCode ?? null,
          eventErrorMessage: e.target.error?.message ?? null,
          inTransition: transitionRef.current !== null,
          hostKind,
          pageOriginHttps,
          detectedOs,
          windowsHost,
        }, errCode === 1 ? 'warn' : 'error');
        logStreamProbe(hostKind);
        }).catch(() => {
          const hostKind = classifyStreamHostKind(src);
          rlog('WAR', 'error', { code: errCode, msg: e.target.error?.message || '', hostKind, pageOriginHttps });
          logAudio('audio error event', {
            eventErrorCode: errCode ?? null,
            eventErrorMessage: e.target.error?.message ?? null,
            inTransition: transitionRef.current !== null,
            hostKind,
            pageOriginHttps,
          }, errCode === 1 ? 'warn' : 'error');
          logStreamProbe(hostKind);
        });
      })
      .catch(() => {
        rlog('WAR', 'error', { code: errCode, msg: e.target.error?.message || '' });
        logAudio('audio error event', {
          eventErrorCode: errCode ?? null,
          eventErrorMessage: e.target.error?.message ?? null,
          inTransition: transitionRef.current !== null,
          pageOriginHttps,
        }, errCode === 1 ? 'warn' : 'error');
      });

    // MEDIA_ERR_ABORTED (1) is normal during track transitions
    if (errCode === 1) return;
    // During a transition, suppress errors from the source swap
    if (transitionRef.current !== null) return;

    // Network renderer active — don't auto-advance locally
    try {
      const az = useMaestroStore.getState().discovery?.activeZone;
      if (az && az.type && az.type !== 'browser' && az.type !== 'local' && az.type !== 'local-native' && az.id !== 'this_computer' && az.id !== 'local_computer' && az.id !== 'this_device') {
        return;
      }
    } catch {}

    const isHls = /\.m3u8(\?|#|$)/i.test(src);
    if (!isHls) {
      const currentTrack = useMaestroStore.getState().currentTrack;
      const isRemoteLibrary =
        currentTrack?.source === 'local_library'
        || /trycloudflare\.com\/stream\//i.test(src);

      // Invalidate cached stream URL so the next attempt re-resolves
      if (src) {
        import('../lib/playback-bridge').then(({ invalidateCachedUrl, clearAllCachedFileUrls }) => {
          invalidateCachedUrl(src);
          if (consecutiveErrorsRef.current >= 2) clearAllCachedFileUrls();
        }).catch(() => {});
      }

      consecutiveErrorsRef.current = (consecutiveErrorsRef.current || 0) + 1;
      if (consecutiveErrorsRef.current > 3) {
        console.error('[WebAudioRenderer] Too many consecutive errors — stopping playback', { count: consecutiveErrorsRef.current, src });
        clearStallWatchdog();
        try { useMaestroStore.getState().setPlaybackState('paused'); } catch {}
        return;
      }

      // Remote home-library streams: re-resolve once (stale/double-wrapped URLs), then pause.
      if (isRemoteLibrary) {
        const track = currentTrack;
        const retryKey = track?.id || track?.file_path || src;
        if (track?.file_path && remoteStreamRetryRef.current !== retryKey) {
          remoteStreamRetryRef.current = retryKey;
          console.warn('[WebAudioRenderer] Remote library stream error — re-resolving URL', { src, errCode });
          import('../lib/playback-bridge').then(async ({ resolveLocalFileUrl, invalidateCachedUrl }) => {
            invalidateCachedUrl(track.file_path);
            invalidateCachedUrl(src);
            try {
              const { getClientTransport } = await import('../handlers/ClientTransportHandler');
              const ws = getClientTransport().getWebSocket?.() ?? null;
              const freshUrl = await resolveLocalFileUrl(track.file_path, ws, track.machine_id);
              const s = useMaestroStore.getState();
              if (s.currentTrack?.id === track.id || s.currentTrack?.file_path === track.file_path) {
                getClientTransport().playLocalStream({ ...track, streamUrl: freshUrl });
              }
            } catch (e) {
              console.warn('[WebAudioRenderer] Remote re-resolve failed — pausing', e?.message || e);
              clearStallWatchdog();
              try { useMaestroStore.getState().setPlaybackState('paused'); } catch {}
            }
          }).catch(() => {});
          return;
        }
        console.warn('[WebAudioRenderer] Remote library stream error — pausing (no auto-advance)', { src, errCode });
        clearStallWatchdog();
        try { useMaestroStore.getState().setPlaybackState('paused'); } catch {}
        return;
      }

      console.error('[WebAudioRenderer] Non-HLS audio error — auto-advancing to next track', { src, errCode, consecutiveErrors: consecutiveErrorsRef.current });
      clearStallWatchdog();
      try {
        import('../transport/QueueController').then(({ QueueController }) => {
          QueueController.next();
        });
      } catch {}
      return;
    }

    setTimeout(() => {
      try {
        const store = useMaestroStore.getState();
        if (store.playbackState !== 'playing') return;
        const audio = audioRef.current;
        if (!audio?.src) return;
        logAudio('retrying HLS audio after error');
        audio.load();
        audio.play()
          .then(() => {
            logAudio('HLS retry play resolved');
            try { clientTransport.startPositionBroadcast(); } catch {}
          })
          .catch((error) => {
            logAudio('HLS retry play rejected', getPlayErrorDetails(error), 'error');
            try { useMaestroStore.getState().setPlaybackState?.('paused'); } catch {}
          });
      } catch {}
    }, 800);
  }, [clientTransport, logAudio]);
  
  /**
   * canplay — if a transition is active, this is the ONLY place that starts
   * playback on the new source. Fade in from 0 → user volume.
   */
  const handleCanPlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    logAudio('canplay fired', {
      pendingPlay: pendingPlayRef.current,
      inTransition: transitionRef.current !== null,
    });

    if (isNetworkRendererActive()) {
      logAudio('canplay blocked — network renderer active, local audio must not play');
      if (!audio.paused) audio.pause();
      pendingPlayRef.current = false;
      useMaestroStore.getState().setLocalRendererSuppressed(true, 'network-zone-active');
      if (transitionRef.current !== null) {
        transitionRef.current = null;
        if (safetyTimerRef.current) { clearTimeout(safetyTimerRef.current); safetyTimerRef.current = null; }
      }
      return;
    }

    if (transitionRef.current !== null) {
      const targetVol = transitionRef.current;
      if (safetyTimerRef.current) { clearTimeout(safetyTimerRef.current); safetyTimerRef.current = null; }
      transitionRef.current = null;
      setStreamBuffering(false);
      audio.volume = 0;
      audio.play()
        .then(() => {
          logAudio('transition play resolved', { targetVolume: targetVol });
          pendingPlayRef.current = false;
          smoothFade(audio, 0, targetVol, FADE_IN_MS, fadeTimerRef);
          try { clientTransport.startPositionBroadcast(); } catch {}
        })
        .catch((error) => {
          logAudio('transition play rejected', {
            targetVolume: targetVol,
            ...getPlayErrorDetails(error),
          }, 'error');
          audio.volume = targetVol;
        });
      return;
    }

    // If a play was blocked by autoplay policy, retry now that the source is loaded.
    // The silent WAV pre-activation may have satisfied the user-gesture requirement.
    if (pendingPlayRef.current && !audio.src.startsWith('data:')) {
      const storeState = useMaestroStore.getState();
      if (storeState.playbackState === 'playing' || storeState.playbackState === 'loading') {
        audio.play()
          .then(() => {
            logAudio('pending play retry resolved', { playbackState: storeState.playbackState });
            pendingPlayRef.current = false;
            try { clientTransport.startPositionBroadcast(); } catch {}
            if (storeState.playbackState !== 'playing') {
              storeState.setPlaybackState('playing');
            }
          })
          .catch((error) => {
            logAudio('pending play retry rejected', {
              playbackState: storeState.playbackState,
              ...getPlayErrorDetails(error),
            }, 'error');
          });
      }
    }
  }, [clientTransport, logAudio, setStreamBuffering]);
  
  const handleLoadStart = useCallback(() => {
    logAudio('loadstart fired');
  }, [logAudio]);
  
  const handlePlaying = useCallback(() => {
    rlog('WAR', 'playing', { t: Math.floor(audioRef.current?.currentTime || 0) });
    logAudio('playing fired');
    consecutiveErrorsRef.current = 0;
    remoteStreamRetryRef.current = null;
    lastProgressTimeRef.current = Date.now();
    clearStallRecoveryTimer();
    stallRecoveryInFlightRef.current = false;
    setStreamBuffering(false);
    syncPositionFromAudio();
    startStallWatchdog();
    try { clientTransport.startPositionBroadcast(); } catch {}
  }, [clientTransport, logAudio, startStallWatchdog, setStreamBuffering, syncPositionFromAudio]);

  // ---------------------------------------------------------------------------
  // MEDIA EVENT INSTRUMENTATION (diagnostic only — no auto-skip)
  // ---------------------------------------------------------------------------

  const buildDiagSnapshot = useCallback(() => {
    const a = audioRef.current;
    if (!a) return {};
    const store = useMaestroStore.getState();
    const buffered = [];
    for (let i = 0; i < a.buffered.length; i++) {
      buffered.push([Math.floor(a.buffered.start(i)), Math.floor(a.buffered.end(i))]);
    }
    return {
      readyState: a.readyState,
      networkState: a.networkState,
      currentTime: Math.floor(a.currentTime),
      duration: Math.floor(a.duration || 0),
      paused: a.paused,
      error: a.error ? { code: a.error.code, message: a.error.message } : null,
      buffered,
      storePlaybackState: store.playbackState,
      activeZoneType: store.discovery?.activeZone?.type ?? null,
      activeZoneId: store.discovery?.activeZone?.id ?? null,
      localSuppressed: store.localRendererSuppressed,
      trackId: store.currentTrack?.id ?? null,
    };
  }, []);

  const handleStalled = useCallback(() => {
    const snap = buildDiagSnapshot();
    console.warn('[WAR:media] stalled', snap);
    rlog('WAR', 'stalled', snap);
    syncPositionFromAudio();
    setStreamBuffering(true);
    clearStallRecoveryTimer();
    stallRecoveryTimerRef.current = setTimeout(() => {
      stallRecoveryTimerRef.current = null;
      attemptStallRecovery();
    }, STALL_RECOVERY_DELAY_MS);
  }, [attemptStallRecovery, buildDiagSnapshot, clearStallRecoveryTimer, setStreamBuffering, syncPositionFromAudio]);

  const handleWaiting = useCallback(() => {
    const snap = buildDiagSnapshot();
    console.warn('[WAR:media] waiting', snap);
    rlog('WAR', 'waiting', snap);
    syncPositionFromAudio();
    setStreamBuffering(true);
  }, [buildDiagSnapshot, setStreamBuffering, syncPositionFromAudio]);

  const handleSuspend = useCallback(() => {
    const snap = buildDiagSnapshot();
    console.log('[WAR:media] suspend', snap);
    rlog('WAR', 'suspend', snap);
  }, [buildDiagSnapshot]);
  
  // ---------------------------------------------------------------------------
  // TRANSPORT EVENT SUBSCRIPTIONS
  // ---------------------------------------------------------------------------
  
  useEffect(() => {
    if (!audioRef.current) return;
    
    const audio = audioRef.current;
    
    audio.volume = Math.max(0, Math.min(1, (storeVolume ?? 100) / 100));
    
    const destroyHls = () => {
      try { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } } catch {}
    };

    const setStreamUrl = (newUrl) => {
      consecutiveErrorsRef.current = 0;
      let url = String(newUrl || '').trim();
      if (!url) return;
      if (url.includes('/stream/')) {
        url = resolveLocalRendererStreamUrl(url, { context: 'WebAudioRenderer.setStreamUrl' });
      }
      logAudio('setStreamUrl called', { newUrl: url });
      try {
        if (!audio.paused) audio.pause();
      } catch { /* ignore */ }
      destroyHls();

      const isHlsStream = /\.m3u8(\?|#|$)/i.test(url);
      if (isHlsStream) {
        const canNative = typeof audio.canPlayType === 'function' && audio.canPlayType('application/vnd.apple.mpegurl');
        if (canNative) {
          logAudio('using native HLS playback', { newUrl: url });
          audio.src = url;
          audio.load();
          return;
        }
        if (Hls && typeof Hls.isSupported === 'function' && Hls.isSupported()) {
          try {
            const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
            hlsRef.current = hls;
            hls.attachMedia(audio);
            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
              logAudio('HLS media attached', { newUrl: url });
              try { hls.loadSource(url); } catch {}
            });
            hls.on(Hls.Events.ERROR, (_evt, data) => {
              logAudio('HLS error event', {
                hlsType: data?.type ?? null,
                hlsDetails: data?.details ?? null,
                fatal: !!data?.fatal,
              }, data?.fatal ? 'error' : 'warn');
              if (!data?.fatal) return;
              try {
                if (data?.type === Hls.ErrorTypes.NETWORK_ERROR) {
                  setTimeout(() => { try { hls.startLoad(-1); audio.play().catch(() => {}); } catch {} }, 750);
                  return;
                }
                if (data?.type === Hls.ErrorTypes.MEDIA_ERROR) {
                  try { hls.recoverMediaError(); audio.play().catch(() => {}); } catch {}
                  return;
                }
              } catch {}
              try { useMaestroStore.getState().setPlaybackState?.('paused'); } catch {}
            });
            return;
          } catch (e) {
            console.error('🔊 [WebAudioRenderer] HLS.js init failed:', e);
          }
        }
      }

      audio.src = url;
      audio.load();
    };

    const hardResetLocalAudio = () => {
      logAudio('hardResetLocalAudio');
      cancelTransition();
      clearStallWatchdog();
      clearStallRecoveryTimer();
      setStreamBuffering(false);
      consecutiveErrorsRef.current = 0;
      pendingPlayRef.current = false;
      destroyHls();
      try { clientTransport.stopPositionBroadcast(); } catch {}
      currentSrcRef.current = '';
      try {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      } catch (e) {
        logAudio('hardResetLocalAudio failed', { error: e?.message || String(e) }, 'warn');
      }
    };

    const commitLocalSource = (url) => {
      currentSrcRef.current = String(url || '');
    };

    TransportCommand.registerLocalAudioReset(hardResetLocalAudio);
    TransportCommand.registerLocalSourceCommit(commitLocalSource);

    // ------------------------------------------------------------------
    // TRACK CHANGED — the transition orchestrator
    // ------------------------------------------------------------------
    const unsubTrack = clientTransport.onTrackChanged((track) => {
      logAudio('trackChanged callback', {
        trackId: track?.id ?? null,
        streamUrl: track?.streamUrl ?? null,
      });
      if (isNetworkRendererActive()) {
        consecutiveErrorsRef.current = 0;
        logAudio('trackChanged blocked — network renderer active, not loading local source');
        useMaestroStore.getState().setLocalRendererSuppressed(true, 'network-zone-active');
        return;
      }
      if (track && track.streamUrl) {
        const newUrl = track.streamUrl;
        
        const prevKey = stripCacheBusters(currentSrcRef.current);
        const nextKey = stripCacheBusters(newUrl);
        const elementKey = stripCacheBusters(audio.src || '');
        if (prevKey === nextKey || elementKey === nextKey) {
          logAudio('trackChanged ignored duplicate source', {
            previousUrl: currentSrcRef.current,
            nextUrl: newUrl,
          });
          currentSrcRef.current = newUrl;
          return;
        }
        currentSrcRef.current = newUrl;

        const isCurrentlyPlaying = !audio.paused && audio.currentTime > 0;
        logAudio('trackChanged accepted source', {
          newUrl,
          isCurrentlyPlaying,
        });

        if (isCurrentlyPlaying) {
          const userVol = Math.max(0.01, (useMaestroStore.getState().volume ?? 100) / 100);

          if (transitionRef.current !== null) {
            // Rapid skip: already mid-transition, volume is already 0 or near 0.
            // Cancel old fade, force silence, swap source immediately.
            if (fadeTimerRef.current) { clearInterval(fadeTimerRef.current); fadeTimerRef.current = null; }
            audio.volume = 0;
            audio.pause();
            transitionRef.current = userVol;
            logAudio('rapid track transition', { targetVolume: userVol });
            setStreamUrl(newUrl);
            // Safety timeout in case canplay never fires
            if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
            safetyTimerRef.current = setTimeout(() => {
              if (transitionRef.current !== null) {
                const vol = transitionRef.current;
                cancelTransition();
                audio.volume = vol;
                logAudio('transition safety timer forcing play', { targetVolume: vol }, 'warn');
                audio.play().catch((error) => {
                  logAudio('transition safety play rejected', {
                    targetVolume: vol,
                    ...getPlayErrorDetails(error),
                  }, 'error');
                });
              }
            }, TRANSITION_SAFETY_MS);
          } else {
            // Normal transition: fade out → pause → swap → canplay handles play + fade in
            transitionRef.current = userVol;
            logAudio('starting normal track transition', { targetVolume: userVol });
            smoothFade(audio, audio.volume, 0, FADE_OUT_MS, fadeTimerRef).then(() => {
              audio.pause();
              setStreamUrl(newUrl);
              // Safety timeout
              safetyTimerRef.current = setTimeout(() => {
                if (transitionRef.current !== null) {
                  const vol = transitionRef.current;
                  cancelTransition();
                  audio.volume = vol;
                  logAudio('normal transition safety timer forcing play', { targetVolume: vol }, 'warn');
                  audio.play().catch((error) => {
                    logAudio('normal transition safety play rejected', {
                      targetVolume: vol,
                      ...getPlayErrorDetails(error),
                    }, 'error');
                  });
                }
              }, TRANSITION_SAFETY_MS);
            });
          }
        } else {
          // Not playing — just swap source directly (no fade needed)
          cancelTransition();
          logAudio('trackChanged direct source swap', { newUrl });
          setStreamUrl(newUrl);
        }
      } else if (!track) {
        logAudio('track cleared');
        currentSrcRef.current = '';
        cancelTransition();
        destroyHls();
        audio.src = '';
        audio.load();
      }
    });
    
    // ------------------------------------------------------------------
    // PLAYBACK STATE — LOCKED OUT during transitions
    // ------------------------------------------------------------------
    const unsubPlayback = clientTransport.onPlaybackStateChanged((state) => {
      rlog('WAR', 'stateChange', { state, paused: audioRef.current?.paused, t: Math.floor(audioRef.current?.currentTime || 0) });
      logAudio('playbackStateChanged callback', {
        requestedState: state,
        pendingPlay: pendingPlayRef.current,
        inTransition: transitionRef.current !== null,
      });
      if (transitionRef.current !== null) {
        logAudio('playback state ignored during transition', { requestedState: state }, 'warn');
        return;
      }

      if (isNetworkRendererActive()) {
        consecutiveErrorsRef.current = 0;
        const currentActiveZone = useMaestroStore.getState().discovery?.activeZone;
        logAudio('playback state mapped to network renderer only', {
          requestedState: state,
          activeZoneId: currentActiveZone?.id ?? null,
          activeZoneType: currentActiveZone?.type ?? null,
        });
        if (!audio.paused) audio.pause();
        pendingPlayRef.current = false;
        useMaestroStore.getState().setLocalRendererSuppressed(true, 'network-zone-active');
        return;
      }
      
      // Zone is local — clear any stale suppression flag
      if (useMaestroStore.getState().localRendererSuppressed) {
        useMaestroStore.getState().setLocalRendererSuppressed(false);
      }

      if (state === 'playing') {
        if (!audio.src) {
          logAudio('playback state ignored because audio has no src', { requestedState: state }, 'warn');
          return;
        }
        // Skip silent WAV data URIs — wait for real source
        if (audio.src.startsWith('data:')) {
          logAudio('playback state ignored because silent pre-activation src is still active', { requestedState: state }, 'warn');
          return;
        }
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              logAudio('playbackState play resolved', { requestedState: state });
              pendingPlayRef.current = false;
              clientTransport.startPositionBroadcast();
            })
            .catch((error) => {
              logAudio('playbackState play rejected', {
                requestedState: state,
                ...getPlayErrorDetails(error),
              }, 'error');
              if (error.name === 'NotAllowedError') {
                console.warn('🔊 [WebAudioRenderer] Autoplay blocked — will retry on canplay');
                pendingPlayRef.current = true;
              }
            });
        }
      } else if (state === 'paused' || state === 'stopped') {
        logAudio('playback state pausing audio', { requestedState: state });
        audio.pause();
        pendingPlayRef.current = false;
        clientTransport.stopPositionBroadcast();
        if (state === 'stopped') {
          logAudio('playback state resetting currentTime to zero');
          audio.currentTime = 0;
        }
      }
    });
    
    // ------------------------------------------------------------------
    // POSITION / SEEK
    // ------------------------------------------------------------------
    const unsubPosition = clientTransport.onPositionChanged((position, duration) => {
      if (audio.src && Math.abs(audio.currentTime - position) > 3) {
        isSeeking.current = true;
        audio.currentTime = position;
        setTimeout(() => { isSeeking.current = false; }, 100);
      }
    });
    
    // ------------------------------------------------------------------
    // VOLUME — yield during transitions so fades aren't stomped
    // ------------------------------------------------------------------
    const unsubVolume = clientTransport.onVolumeChanged((level, muted) => {
      const normalizedVolume = Math.max(0, Math.min(1, level / 100));
      if (transitionRef.current === null && !fadeTimerRef.current) {
        audio.volume = normalizedVolume;
      }
      if (transitionRef.current === null) {
        audio.muted = muted;
      }
    });
    
    return () => {
      TransportCommand.unregisterLocalAudioReset();
      TransportCommand.unregisterLocalSourceCommit();
      unsubTrack();
      unsubPlayback();
      unsubPosition();
      unsubVolume();
      destroyHls();
      cancelTransition();
      clearStallWatchdog();
      clearStallRecoveryTimer();
      setStreamBuffering(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientTransport]);
  
  // ---------------------------------------------------------------------------
  // RECONCILER: recover from paused-while-playing desync (heavily gated)
  // ---------------------------------------------------------------------------

  const lastUserPauseRef = useRef(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Track explicit user pauses so we don't fight them
    const markUserPause = () => { lastUserPauseRef.current = Date.now(); };
    audio.addEventListener('pause', markUserPause);

    const reconcileTimer = setInterval(() => {
      const store = useMaestroStore.getState();
      const a = audioRef.current;
      if (!a) return;

      // Gate 1: store must say 'playing'
      if (store.playbackState !== 'playing') return;
      // Gate 2: audio must actually be paused (the desync condition)
      if (!a.paused) return;
      // Gate 3: not intentionally suppressed for network renderer
      if (store.localRendererSuppressed) return;
      // Gate 4: current zone must be local (not a network renderer)
      if (isNetworkRendererActive()) return;
      // Gate 5: user didn't pause recently (2s debounce)
      if (Date.now() - lastUserPauseRef.current < 2000) return;
      // Gate 6: no track transition in progress
      if (transitionRef.current !== null) return;
      // Gate 7: document must be visible (respect background tab rules)
      if (document.visibilityState !== 'visible') return;
      // Gate 8: audio element must have a real source loaded
      if (!a.src || a.src.startsWith('data:')) return;

      console.warn('[WAR:reconciler] Recovered from paused-while-playing desync', {
        readyState: a.readyState,
        networkState: a.networkState,
        currentTime: a.currentTime,
        src: a.src.substring(0, 80),
      });
      rlog('WAR', 'reconciler-recovery', { rs: a.readyState, ns: a.networkState, t: Math.floor(a.currentTime) });
      a.play().catch((err) => {
        console.warn('[WAR:reconciler] play() rejected, correcting store to paused', err.name);
        try { useMaestroStore.getState().setPlaybackState('paused'); } catch {}
      });
    }, 3000);

    return () => {
      clearInterval(reconcileTimer);
      audio.removeEventListener('pause', markUserPause);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  
  return (
    <audio
      ref={audioRef}
      data-maestro-music="true"
      autoPlay={false}
      onTimeUpdate={handleTimeUpdate}
      onEnded={handleEnded}
      onError={handleError}
      onCanPlay={handleCanPlay}
      onLoadStart={handleLoadStart}
      onPlaying={handlePlaying}
      onStalled={handleStalled}
      onWaiting={handleWaiting}
      onSuspend={handleSuspend}
      preload="auto"
      playsInline
      style={{ display: 'none' }}
    />
  );
};

export default WebAudioRenderer;
